import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, TextField, Button,
  Avatar, Snackbar, Alert, CircularProgress, Divider,
  Switch, FormControlLabel, Select, MenuItem, FormControl, InputLabel,
  Chip, Autocomplete, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Grid
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Layout from '../components/Layout';
import api from '../services/api';

const SECTION_LABEL_SX = {
  color: '#1a237e', fontSize: 12, fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: 1,
  display: 'block', mb: 1.5,
};

export default function CompanyProfile() {
  const [form, setForm] = useState({});
  const [originalForm, setOriginalForm] = useState({});
  const [prefs, setPrefs] = useState(null);
  const [originalPrefs, setOriginalPrefs] = useState(null);
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [optouts, setOptouts] = useState([]);
  const [homeowners, setHomeowners] = useState([]);
  const [optoutToAdd, setOptoutToAdd] = useState(null);
  const [planState, setPlanState] = useState(null);
  const [allPlans, setAllPlans] = useState([]);
  const [upgradeDialog, setUpgradeDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  useEffect(() => {
    api.get('/subcontractor-profile')
      .then(r => {
        const data = r.data || {};
        setForm(data);
        setOriginalForm(data);
      })
      .catch(() => setSnack({ open: true, msg: 'Failed to load profile', sev: 'error' }))
      .finally(() => setLoading(false));

    // Load notification prefs, opt-outs, and homeowners list
    api.get('/sub-prefs').then(r => { setPrefs(r.data); setOriginalPrefs(r.data); }).catch(() => {});
    api.get('/sub-prefs/optouts').then(r => setOptouts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/sub-prefs/homeowners').then(r => setHomeowners(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/subscription/me').then(r => setPlanState(r.data)).catch(() => {});
    api.get('/subscription/plans').then(r => setAllPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const isDirty = JSON.stringify(form) !== JSON.stringify(originalForm);

  const handleLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('logo_url', r.data.url);
      setSnack({ open: true, msg: 'Logo uploaded', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Logo upload failed', sev: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await api.put('/subcontractor-profile', form);
      const data = r.data || form;
      setForm(data);
      setOriginalForm(data);
      setSnack({ open: true, msg: 'Profile saved', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Failed to save profile', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updatePref = (key, value) => setPrefs(p => ({ ...p, [key]: value }));

  const prefsDirty = prefs && originalPrefs && JSON.stringify(prefs) !== JSON.stringify(originalPrefs);

  const savePrefs = async () => {
    if (!prefsDirty) return;
    setPrefsSaving(true);
    try {
      const r = await api.put('/sub-prefs', {
        daily_digest: prefs.daily_digest,
        due_soon_reminder: prefs.due_soon_reminder,
        past_due_reminder: prefs.past_due_reminder,
        default_payment_terms_days: Number(prefs.default_payment_terms_days),
      });
      setPrefs(r.data); setOriginalPrefs(r.data);
      setSnack({ open: true, msg: 'Notification settings saved', sev: 'success' });
    } catch {
      setSnack({ open: true, msg: 'Failed to save settings', sev: 'error' });
    } finally {
      setPrefsSaving(false);
    }
  };

  const addOptout = async () => {
    if (!optoutToAdd) return;
    try {
      await api.post('/sub-prefs/optouts', { homeowner_id: optoutToAdd.id });
      const r = await api.get('/sub-prefs/optouts');
      setOptouts(Array.isArray(r.data) ? r.data : []);
      setOptoutToAdd(null);
    } catch {
      setSnack({ open: true, msg: 'Failed to add opt-out', sev: 'error' });
    }
  };

  const removeOptout = async (id) => {
    try {
      await api.delete(`/sub-prefs/optouts/${id}`);
      setOptouts(optouts.filter(o => o.id !== id));
    } catch {
      setSnack({ open: true, msg: 'Failed to remove opt-out', sev: 'error' });
    }
  };

    const handleCancel = () => {
    setForm(originalForm);
  };

  const tf = (key, label, opts = {}) => (
    <TextField
      label={label}
      value={form[key] || ''}
      onChange={e => set(key, e.target.value)}
      fullWidth size="small"
      {...opts}
    />
  );

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a237e', fontSize: 32 }}>Company Profile</Typography>
        <Typography variant="body2" color="text.secondary">This info appears on your branded invoices.</Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, maxWidth: 880 }}>
          <Box sx={{ p: 4 }}>

            {/* ===== Branding ===== */}
            <Typography sx={SECTION_LABEL_SX}>Branding</Typography>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
                <Avatar
                  src={form.logo_url || undefined}
                  variant="rounded"
                  sx={{
                    width: 80, height: 80,
                    bgcolor: form.logo_url ? 'transparent' : '#f5f6fa',
                    color: '#9e9e9e',
                    border: '1px dashed #c5cae9',
                    borderRadius: 2,
                  }}
                >
                  {!form.logo_url && <BusinessIcon sx={{ fontSize: 32 }} />}
                </Avatar>
                <Button component="label" size="small" variant="outlined"
                  startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                  disabled={uploading}
                  sx={{ borderRadius: 1.5, textTransform: 'none', fontSize: 11, mt: 1, minWidth: 80, py: 0.5 }}>
                  {uploading ? '...' : 'Upload'}
                  <input type="file" hidden accept="image/png,image/jpeg" onChange={handleLogo} />
                </Button>
              </Box>
              <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                {tf('company_name', 'Company Name')}
                {tf('license_number', 'License Number', { placeholder: 'e.g. 1029384' })}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* ===== Tax & Compliance ===== */}
            <Typography sx={SECTION_LABEL_SX}>Tax &amp; Compliance</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
              {tf('tax_id', 'Tax ID / EIN', { placeholder: 'XX-XXXXXXX' })}
              {tf('insurance_number', 'Insurance Number', { placeholder: 'Policy #' })}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* ===== Contact ===== */}
            <Typography sx={SECTION_LABEL_SX}>Contact</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
              {tf('phone', 'Phone')}
              <TextField
                label="Email (login)"
                value={form.email || ''}
                fullWidth size="small" disabled
                helperText="Managed via your account; not editable here"
              />
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.6fr 0.8fr', gap: 1.5 }}>
              {tf('address', 'Street Address')}
              {tf('city', 'City')}
              {tf('state', 'State')}
              {tf('zip_code', 'ZIP')}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* ===== Payments ===== */}
            <Typography sx={SECTION_LABEL_SX}>Payments</Typography>
            <TextField
              label="Payment Instructions"
              value={form.payment_instructions || ''}
              onChange={e => set('payment_instructions', e.target.value)}
              fullWidth multiline rows={3}
              placeholder="e.g. Make checks payable to Sarah Contracting LLC. Mail to address above."
              helperText="Shown at the bottom of every invoice you send."
            />

            <Divider sx={{ my: 3 }} />

            {/* ===== Notifications & Reminders ===== */}
            <Typography sx={SECTION_LABEL_SX}>Notifications &amp; Reminders</Typography>
            {!prefs ? (
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            ) : (
              <Box>
                {/* Toggles */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={!!prefs.daily_digest} onChange={e => updatePref('daily_digest', e.target.checked)} />}
                    label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Daily aging digest</Typography><Typography variant="caption" color="text.secondary">Yaplar emails you a summary of unpaid invoices every morning.</Typography></Box>}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!prefs.due_soon_reminder} onChange={e => updatePref('due_soon_reminder', e.target.checked)} />}
                    label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>7-day heads-up to homeowner</Typography><Typography variant="caption" color="text.secondary">Yaplar emails the homeowner 7 days before due. You're cc'd on every reminder.</Typography></Box>}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                  <FormControlLabel
                    control={<Switch checked={!!prefs.past_due_reminder} onChange={e => updatePref('past_due_reminder', e.target.checked)} />}
                    label={<Box><Typography variant="body2" sx={{ fontWeight: 600 }}>Past-due reminder to homeowner</Typography><Typography variant="caption" color="text.secondary">Yaplar emails the homeowner once the invoice is past its due date. You're cc'd.</Typography></Box>}
                    sx={{ alignItems: 'flex-start', m: 0 }}
                  />
                </Box>

                {/* Payment terms */}
                <Box sx={{ mb: 2, maxWidth: 280 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Default payment terms</InputLabel>
                    <Select
                      value={prefs.default_payment_terms_days ?? 30}
                      label="Default payment terms"
                      onChange={e => updatePref('default_payment_terms_days', e.target.value)}
                    >
                      <MenuItem value={0}>Immediate</MenuItem>
                      <MenuItem value={7}>Net 7</MenuItem>
                      <MenuItem value={15}>Net 15</MenuItem>
                      <MenuItem value={30}>Net 30</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Used to compute the due date on new invoices.
                  </Typography>
                </Box>

                {/* Save row for prefs */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                  <Button variant="outlined" size="small" onClick={savePrefs}
                    disabled={!prefsDirty || prefsSaving}
                    sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#1a237e', color: '#1a237e' }}>
                    {prefsSaving ? 'Saving…' : 'Save notification settings'}
                  </Button>
                </Box>

                {/* Opt-out list */}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Don't auto-email these customers</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Yaplar will skip homeowner reminders for anyone on this list. Your daily digest is unaffected.
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                    {optouts.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">No opt-outs yet.</Typography>
                    ) : optouts.map(o => (
                      <Chip key={o.id}
                        label={`${o.first_name || ''} ${o.last_name || ''}`.trim() || o.email}
                        onDelete={() => removeOptout(o.id)}
                        sx={{ bgcolor: '#f5f6fa', fontWeight: 500 }} />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Autocomplete
                      options={homeowners.filter(h => !optouts.some(o => o.homeowner_id === h.id))}
                      getOptionLabel={(h) => `${h.first_name || ''} ${h.last_name || ''}`.trim() + (h.email ? ` (${h.email})` : '')}
                      value={optoutToAdd}
                      onChange={(e, v) => setOptoutToAdd(v)}
                      renderInput={(params) => <TextField {...params} size="small" label="Pick a customer" />}
                      sx={{ width: 320 }}
                      noOptionsText="No customers to add"
                    />
                    <Button variant="text" size="small" onClick={addOptout} disabled={!optoutToAdd}
                      sx={{ color: '#1a237e' }}>Add</Button>
                  </Box>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            {/* ===== Billing & Plan ===== */}
            <Typography sx={SECTION_LABEL_SX}>Billing &amp; Plan</Typography>
            {!planState || !planState.has_subscription ? (
              <Typography variant="caption" color="text.secondary">Loading…</Typography>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 11 }}>Current plan</Typography>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1a237e', mt: 0.5 }}>
                      {planState.plan.name}
                      {planState.plan.monthly_price_cents > 0 && (
                        <Typography component="span" sx={{ fontSize: 14, color: '#666', fontWeight: 500, ml: 1 }}>
                          · ${(planState.plan.monthly_price_cents / 100).toFixed(0)}/mo
                        </Typography>
                      )}
                    </Typography>
                    {planState.subscription.status === 'trial' && planState.days_until_trial_end != null && (
                      <Typography variant="caption" sx={{ color: planState.days_until_trial_end < 14 ? '#c62828' : '#666', fontWeight: 600 }}>
                        {planState.trial_expired
                          ? '⚠ Trial expired'
                          : `Trial: ${planState.days_until_trial_end} day${planState.days_until_trial_end === 1 ? '' : 's'} remaining`}
                      </Typography>
                    )}
                  </Box>
                  <Button variant="contained" onClick={() => setUpgradeDialog(true)}
                    sx={{ bgcolor: '#1a237e', borderRadius: 2, textTransform: 'none', px: 3 }}>
                    View Plans
                  </Button>
                </Box>

                {/* Usage bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Active contracts</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: !planState.can_create_contract ? '#c62828' : '#1a237e' }}>
                      {planState.usage.active_contracts} / {planState.effective_limit ?? '∞'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={planState.effective_limit ? Math.min(100, (planState.usage.active_contracts / planState.effective_limit) * 100) : 0}
                    sx={{
                      height: 8, borderRadius: 4, bgcolor: '#eceff1',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: !planState.can_create_contract ? '#c62828' : (planState.effective_limit && planState.usage.active_contracts / planState.effective_limit > 0.8 ? '#ef9f27' : '#2e7d32')
                      }
                    }}
                  />
                </Box>

                {!planState.can_create_contract && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>You've reached your plan limit</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {planState.create_block_reason}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

                        {/* ===== Actions ===== */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
              <Button variant="text" onClick={handleCancel} disabled={!isDirty || saving}
                sx={{ borderRadius: 2, px: 3, textTransform: 'none', color: '#666' }}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving || !isDirty}
                sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 4, textTransform: 'none' }}>
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </Box>

          </Box>
        </Card>
      )}

      
      {/* Plans dialog */}
      <Dialog open={upgradeDialog} onClose={() => setUpgradeDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: '#1a237e', borderBottom: '1px solid #e0e0e0' }}>
          Yaplar Plans
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
            Pick the plan that fits your business. Contact us to upgrade.
          </Typography>
          <Grid container spacing={2}>
            {allPlans.map(p => {
              const isCurrent = planState?.plan?.id === p.id;
              return (
                <Grid item xs={12} sm={6} md={3} key={p.id}>
                  <Card variant="outlined" sx={{
                    p: 2.5, height: '100%',
                    borderColor: isCurrent ? '#1a237e' : '#e0e0e0',
                    borderWidth: isCurrent ? 2 : 1,
                    bgcolor: isCurrent ? '#f8f9ff' : 'white',
                  }}>
                    <Typography variant="caption" sx={{ color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {p.slug}
                    </Typography>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1a237e', mt: 0.5 }}>
                      {p.name}
                    </Typography>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: '#1a237e', mt: 1 }}>
                      ${(p.monthly_price_cents / 100).toFixed(0)}
                      <Typography component="span" sx={{ fontSize: 13, color: '#666', fontWeight: 500 }}>/mo</Typography>
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, mb: 2, color: '#555' }}>
                      {p.max_active_contracts ?? '∞'} active contracts
                    </Typography>
                    <Box sx={{ borderTop: '1px solid #eee', pt: 1.5 }}>
                      {Object.entries(p.feature_flags || {}).filter(([k, v]) => v).map(([k]) => (
                        <Typography key={k} variant="caption" sx={{ display: 'block', color: '#444' }}>
                          ✓ {k.replace(/_/g, ' ')}
                        </Typography>
                      ))}
                    </Box>
                    {isCurrent && (
                      <Box sx={{ mt: 2, textAlign: 'center', bgcolor: '#1a237e', color: 'white', py: 0.75, borderRadius: 1.5, fontSize: 12, fontWeight: 700 }}>
                        Current
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={() => setUpgradeDialog(false)}>Close</Button>
          <Button variant="contained" disabled
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>
            Subscribe (coming soon)
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
      </Snackbar>
    </Layout>
  );
}
